import apiClient from "./client";

// 徽章类型定义
export interface Badge {
    id: number;
    name: string;
    icon_undiscovered: string;
    icon_received: string;
}

export interface UserBadge {
    badge: number;
    create_time: string;
}

export interface BadgeData {
    badges: Badge[];
    user_badges: UserBadge[];
}

// 用户关注相关类型定义
export interface UserProfile {
    avatar: string | null;
}

export interface FollowUser {
    id: number;
    profile: UserProfile;
    username: string;
}

export interface FollowData {
    next: string | null;
    previous: string | null;
    results: FollowUser[];
}



export const userService = {
    async sendVerifyCode(data: { phone_number: string; op?: number }): Promise<any> {
        const requestData: any = { phone_number: data.phone_number };
        if (data.op !== undefined) {
            requestData.op = data.op;
        }
        const response = await apiClient.post("/api/user/send-sms-verification-code/", requestData);
        return response;
    },
    async register(data: any): Promise<any> {
        const response = await apiClient.post("/api/user/register/", data);
        return response;
    },
    // Etapa-1 phone-availability probe (anonymous, throttled). Body accepts a
    // masked or E.164 BR number — the backend's PhoneNumberField normalizes via
    // PHONENUMBER_DEFAULT_REGION='BR', so a formatted number and its raw form
    // resolve to the same key. Response carries only { registered: boolean }.
    async checkPhone(phoneNumber: string): Promise<any> {
        const response = await apiClient.post("/api/user/check-phone/", { phone_number: phoneNumber });
        return response;
    },
    async setUserInfo(data: any): Promise<any> {
        const response = await apiClient.patch("/api/user/update-profile/", data);
        return response;
    },
    async getUserData(): Promise<any> {
        const response = await apiClient.get("/api/user/info/");
        return response;
    },
    // 获取当前登录用户的基本信息（用于拿到用户ID等轻量字段）
    async getUserInfoId(): Promise<any> {
        const response = await apiClient.get("/api/user/info/");
        return response;
    },
    // 通过用户ID获取用户完整信息
    async getUserById(userId: number | string): Promise<any> {
        const response = await apiClient.get(`/api/user/${userId}/`);
        return response;
    },
    async login({ phoneNumber, password }: { phoneNumber: string; password: string }): Promise<any> {
        const response = await apiClient.post("/api/user/login/", { phone_number: `+${phoneNumber}`, password });
        return response;
    },
    async logout(): Promise<any> {
        const response = await apiClient.post("/api/user/logout/");
        return response;
    },
    async verifyCode(data: { phone_number: string; verification_code: string; op?: number }): Promise<any> {
        const requestData: any = { 
            phone_number: data.phone_number, 
            verification_code: data.verification_code 
        };
        if (data.op !== undefined) {
            requestData.op = data.op;
        }
        const response = await apiClient.post("/api/user/verify-code/", requestData);
        return response;
    },
    async savePassword(data: { password: string; token: string }): Promise<any> {
        const response = await apiClient.post("/api/user/reset-password/", data);
        return response;
    },
    async searchUser(phoneNumber: string): Promise<any> {
        const data = { phone_number: phoneNumber };
        const response = await apiClient.post("/api/user/search/", data);
        return response;
    },
    async getNotifications(isRead: boolean | null): Promise<any> {
        const params: any = {};
        if (isRead !== null) {
            params.is_read = isRead;
        }
        const response = await apiClient.get("/api/notification/", { params });
        return response;
    },
    async readNotifications(idList: number[]): Promise<any> {
        const response = await apiClient.post("/api/notification/read/", { id_list: idList });
        return response;
    },
    async readAllNotifications(): Promise<any> {
        const response = await apiClient.post("/api/notification/read-all/");
        return response;
    },
    async getStreak(): Promise<any> {
        const response = await apiClient.get("/api/user/streak/");
        return response;
    },
    async getBadges(userId: number | string): Promise<any> {
        const response = await apiClient.get(`/api/user/${userId}/badges/`);
        return response;
    },
    async getFriends(): Promise<any> {
        const response = await apiClient.get("/api/user/friends/");
        return response;
    },
    async followUser(userId: number | string): Promise<any> {
        const response = await apiClient.post(`/api/user/${userId}/follow/`);
        return response;
    },
    async updateAvatar(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('avatar', file);
        const response = await apiClient.patch("/api/user/update-profile/", formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    },
    async unfollowUser(userId: number | string): Promise<any> {
        const response = await apiClient.post(`/api/user/${userId}/unfollow/`);
        return response;
    },
    async getFollowersList(userId: number | string): Promise<any> {
        const response = await apiClient.get(`/api/user/${userId}/followers/`);
        return response;
    },
    async getFollowingList(userId: number | string): Promise<any> {
        const response = await apiClient.get(`/api/user/${userId}/following/`);
        return response;
    },
    async sendVerificationEmail(): Promise<any> {
        const response = await apiClient.post("/api/user/send-verification-email/");
        return response;
    },
    async uploadMedia(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post("/api/upload-media/", formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    },
    async verifyProfile(photo: File, token: string): Promise<any> {
        const formData = new FormData();
        formData.append('photo', photo);
        formData.append('token', token);
        const response = await apiClient.post("/api/user/verify/", formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    },
    async getUserBalance(): Promise<any> {
        const response = await apiClient.get("/api/user/balance/");
        return response;
    },

    // Bundle 4c — MLM referral endpoints
    async getReferralStats(): Promise<any> {
        return await apiClient.get("/api/user/referral-stats/");
    },
    async markReferralsVisited(): Promise<any> {
        return await apiClient.post("/api/user/mark-referrals-visited/");
    },
    async trackReferralShare(shareChannel: 'whatsapp' | 'copy' | 'email' | 'other'): Promise<any> {
        return await apiClient.post("/api/affiliate/referral/track-share/", { share_channel: shareChannel });
    },

}